// The catalog Intelligence rail's width in the pre-visit's right column (`layoutFixes`; Antonin, 4 Nov: "Scrips Intelligence is
// clipped"). His rail draws at its own 380, but where the pre-visit's right column is inline (from 1384, the bridge's rule) the page
// gives the middle group a fixed share and leaves the right column `--enc-right-width` (320, as wide as the left panel); the body
// clips the rest. At 1440 the rail ran ~60 past the edge, cutting the Next best action text, the times and the close button. Here
// the rail takes the room the column really has: measured (the body's right edge less the middle group's), never parsed from a
// token, so any unit or calc() in the token is fine. Below 1384, or outside the pre-visit body, nothing changes (the rail fits;
// measured at 1280).
import { useState, useSyncExternalStore } from 'react';

/** Where the bridge makes the pre-visit's right column inline (`showcase-bridge.css`, "Right panel balanced"). */
export const INLINE_RIGHT_COLUMN_QUERY = '(min-width: 1384px)';
const BODY = '[data-slot="pre-encounter-body"]';
const RAIL_IN_PRE_VISIT_BODY = `${BODY} > [data-slot="intelligence-rail-catalog"]`;
const MIDDLE_GROUP = ':scope > [data-slot="pre-encounter-main-group"]';

/** The right column's measured width in whole px when the rail sits in the pre-visit body with the column inline; otherwise
 *  undefined. Rounded down, so a sub-pixel remainder never pushes the rail past the edge. The middle group's width does not depend
 *  on the rail there (the bridge fixes it), so giving the rail this width cannot feed back into the measurement. */
export function readPreVisitRailWidth(doc: Document = document, win: Window = window): number | undefined {
  const rail = doc.querySelector(RAIL_IN_PRE_VISIT_BODY);
  if (!rail?.parentElement) return undefined;
  if (!win.matchMedia?.(INLINE_RIGHT_COLUMN_QUERY).matches) return undefined;
  const body = rail.parentElement;
  const middle = body.querySelector(MIDDLE_GROUP);
  if (!middle) return undefined;
  const room = Math.floor(body.getBoundingClientRect().right - middle.getBoundingClientRect().right);
  return room > 0 ? room : undefined;
}

/** A width store per rail: the measurement runs in the watchers (after mount, on a window resize, and when the body or its middle
 *  group change size: the left panel folding, the page settling), never on a render; a render reads the cached number. Subscribed
 *  after mount, so the body is in the page by then. */
function columnWidthStore() {
  let width: number | undefined;
  const subscribe = (notify: () => void) => {
    const update = () => {
      const next = readPreVisitRailWidth();
      if (next !== width) {
        width = next;
        notify();
      }
    };
    update();
    window.addEventListener('resize', update);
    const body = document.querySelector(BODY);
    const observer = body && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (observer && body) {
      observer.observe(body);
      const middle = body.querySelector(MIDDLE_GROUP);
      if (middle) observer.observe(middle);
    }
    return () => {
      window.removeEventListener('resize', update);
      observer?.disconnect();
    };
  };
  return { subscribe, get: () => width };
}

const OFF = { subscribe: () => () => {}, get: (): number | undefined => undefined };

/** The rail's width in the pre-visit's inline right column, kept current; undefined elsewhere (his own width). Off: no listener, no
 *  observer, no measurement. */
export function usePreVisitRailWidth(on: boolean): number | undefined {
  const [store] = useState(columnWidthStore);
  const active = on ? store : OFF;
  return useSyncExternalStore(active.subscribe, active.get, OFF.get);
}
